import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Check, X, Loader2, AlertTriangle } from "lucide-react";

const inputCls = "h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";
const errMsg = (e, d) => formatApiError(e.response?.data?.detail) || d;

function CategoryRow({ c, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c.name);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    const ok = await onRename(c, name);
    setSaving(false);
    if (ok) setEditing(false);
  };
  return (
    <div className="flex items-center gap-3 rounded-lg border border-white/[0.04] px-4 py-2.5" data-testid={`category-${c.id}`}>
      {editing ? (
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className="h-9 flex-1 rounded-md border-white/[0.1] bg-[#0A0A0A] text-white" data-testid={`rename-input-${c.id}`} autoFocus />
      ) : (
        <span className="flex-1 text-sm text-white" data-testid={`category-name-${c.id}`}>
          {c.name}
          {c.is_default && <span className="ml-2 text-[10px] text-neutral-600">default</span>}
        </span>
      )}
      <span className="text-xs text-neutral-500" data-testid={`category-count-${c.id}`}>{c.resource_count} {c.resource_count === 1 ? "resource" : "resources"}</span>
      {editing ? (
        <>
          <button onClick={save} disabled={saving} className="text-emerald-400 hover:text-emerald-300" data-testid={`rename-save-${c.id}`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}</button>
          <button onClick={() => { setEditing(false); setName(c.name); }} className="text-neutral-500 hover:text-white" data-testid={`rename-cancel-${c.id}`}><X className="h-4 w-4" /></button>
        </>
      ) : (
        <>
          <button onClick={() => setEditing(true)} className="text-neutral-500 hover:text-white" title="Rename" data-testid={`rename-category-${c.id}`}><Pencil className="h-4 w-4" /></button>
          <button onClick={() => onDelete(c)} className="text-neutral-500 hover:text-[#FF6069]" title="Delete" data-testid={`del-category-${c.id}`}><Trash2 className="h-4 w-4" /></button>
        </>
      )}
    </div>
  );
}

function DeleteCategoryDialog({ target, categories, onClose, onDone }) {
  const [dest, setDest] = useState("");
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const others = categories.filter((c) => c.id !== target.id);
  const populated = target.resource_count > 0;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/resources/categories/${target.id}`, { params: populated ? { move_to: dest } : {} });
      toast.success(data.moved ? `Category deleted. ${data.moved} resource(s) moved.` : "Category deleted");
      onDone();
    } catch (e) { toast.error(errMsg(e, "Failed to delete category")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/[0.08] bg-[#141414] text-white" data-testid="delete-category-dialog">
        <DialogHeader>
          <DialogTitle>Delete "{target.name}"?</DialogTitle>
          <DialogDescription className="text-neutral-500">
            {populated ? `This category has ${target.resource_count} resource(s). They will be moved to the category you choose, not deleted.` : "This category has no resources and will be removed."}
          </DialogDescription>
        </DialogHeader>
        {populated && (others.length === 0 ? (
          <p className="text-sm text-[#FF6069]" data-testid="no-destination-msg">Create another category first. Resources need somewhere to move.</p>
        ) : (
          <Select value={dest} onValueChange={setDest}>
            <SelectTrigger className={inputCls} data-testid="move-to-select"><SelectValue placeholder="Move resources to..." /></SelectTrigger>
            <SelectContent className="max-h-60 border-white/[0.08] bg-[#141414] text-white">
              {others.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
        <DialogFooter>
          <button onClick={onClose} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]" data-testid="cancel-delete-category">Cancel</button>
          <button onClick={run} disabled={busy || (populated && !dest)} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-50" data-testid="confirm-delete-category">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{populated ? "Move & Delete" : "Delete"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DuplicateBanner({ categories, onDone }) {
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const seen = new Set();
  const hasDup = categories.some((c) => { const k = c.name.trim().toLowerCase().replace(/\s+/g, " "); if (seen.has(k)) return true; seen.add(k); return false; });
  if (!hasDup) return null;
  const review = async () => { try { const { data } = await api.get("/admin/resources/categories/duplicates"); setInv(data); } catch (e) { toast.error(errMsg(e, "Failed")); } };
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/resources/categories/dedupe", { confirm: true });
      toast.success(`Cleanup complete: ${data.deleted} duplicate categories removed`);
      setInv(null); onDone();
    } catch (e) { toast.error(errMsg(e, "Cleanup failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4" data-testid="duplicate-banner">
      <p className="flex items-center gap-2 text-sm text-amber-300"><AlertTriangle className="h-4 w-4" /> Duplicate categories found.</p>
      {!inv ? (
        <button onClick={review} className="mt-2 text-sm font-medium text-white underline" data-testid="review-duplicates-btn">Review cleanup plan</button>
      ) : (
        <div className="mt-2 space-y-2 text-sm text-neutral-300" data-testid="duplicate-plan">
          <p>{inv.duplicate_groups} duplicate groups · {inv.records_to_remove} extra records to remove · {inv.resources_to_reassign} {inv.resources_to_reassign === 1 ? "resource" : "resources"} to reassign. No resources or files are deleted.</p>
          <button onClick={run} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-4 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-50" data-testid="run-dedupe-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Clean Up Duplicates</button>
        </div>
      )}
    </div>
  );
}

export function CategoryManager({ categories, reload }) {
  const [newCat, setNewCat] = useState("");
  const [adding, setAdding] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const add = async () => {
    if (!newCat.trim() || adding) return;
    setAdding(true);
    try { await api.post("/admin/resources/categories", { name: newCat }); toast.success("Category added"); setNewCat(""); reload(); }
    catch (e) { toast.error(errMsg(e, "Failed to add category")); }
    finally { setAdding(false); }
  };
  const rename = async (c, name) => {
    try { await api.put(`/admin/resources/categories/${c.id}`, { name }); toast.success("Category renamed"); reload(); return true; }
    catch (e) { toast.error(errMsg(e, "Failed to rename")); return false; }
  };

  return (
    <div className="max-w-2xl rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="category-manager">
      <DuplicateBanner categories={categories} onDone={reload} />
      <div className="mb-4 flex gap-2">
        <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New category name" className={inputCls} data-testid="new-category-input" />
        <button onClick={add} disabled={adding} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-4 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-50" data-testid="add-category-btn"><Plus className="h-4 w-4" /> Add</button>
      </div>
      <div className="space-y-1">
        {categories.map((c) => <CategoryRow key={`${c.id}-${c.name}`} c={c} onRename={rename} onDelete={setDelTarget} />)}
      </div>
      <DeleteCategoryDialog key={delTarget?.id} target={delTarget} categories={categories} onClose={() => setDelTarget(null)} onDone={() => { setDelTarget(null); reload(); }} />
    </div>
  );
}
